import CloseIcon from "@mui/icons-material/Close";
import { Box, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import React, { MutableRefObject } from "react";
import { useTranslation } from "react-i18next";
import { PublishedCard } from "../types";
import TemplateValidationMessages from "./template/TemplateValidationMessages";
import UseTemplatePageRenderer from "./template/UseTemplatePageRenderer";
import type { PageData } from "../utils/useTemplatePageHelpers";
import {
  formatCreateTaasCardPageType,
  getCreateTaasCardTemplateFlags,
  isEndingImagePage,
  isRemovablePage,
  type AddablePageType,
  type PageLockConfig,
} from "../utils/createTaasCardPages";
import { AddTaasCardPageSection } from "./AddTaasCardPageSection";

interface CreateTaasCardDetailsFormProps {
  card: PublishedCard;
  pages: PageData[];
  isUpdatingRef: MutableRefObject<boolean>;
  updatePageData: (pageIndex: number, newContent: any) => void;
  pageValidationMessages: string[][];
  colors: {
    title: string;
    body: string;
  };
  darkMode: boolean;
  addablePageTypes: AddablePageType[];
  onAddPage: (type: AddablePageType) => void;
  pageLockConfig: PageLockConfig | null;
  onRemovePage: (pageIndex: number) => void;
  isEditMode?: boolean;
}

/** Step 3 — editable template pages (same fields as Use Template flow). */
export function CreateTaasCardDetailsForm({
  card,
  pages,
  isUpdatingRef,
  updatePageData,
  pageValidationMessages,
  colors,
  darkMode,
  addablePageTypes,
  onAddPage,
  pageLockConfig,
  onRemovePage,
  isEditMode = false,
}: CreateTaasCardDetailsFormProps) {
  const { t } = useTranslation();
  const { usesEndingImage } = getCreateTaasCardTemplateFlags(card, t);

  if (pages.length === 0) {
    return (
      <Typography sx={{ fontSize: "0.875rem", color: colors.body, py: 2 }}>
        {t("no_card_data_found", { ns: "taascard" }) || "No card data found"}
      </Typography>
    );
  }

  // The add-page section is rendered just before the ending image (or at the
  // very end when no ending image exists). The `+` chip therefore "shifts
  // down" naturally as new pages are inserted above it.

  const lastIdx = pages.length - 1;
  const lastIsEnding = isEndingImagePage(pages[lastIdx]);
  const addSectionAfterIdx = lastIsEnding ? lastIdx - 1 : lastIdx;
  const showAddSection = !isEditMode && addablePageTypes.length > 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderRadius: 2,
          bgcolor: darkMode ? "var(--neutral-800)" : "var(--primary-100-light)",
          border: `1px solid ${darkMode ? "var(--neutral-700)" : "var(--primary-200)"}`,
        }}
      >
        <Typography sx={{ fontSize: "0.75rem", color: colors.body }}>
          Template
        </Typography>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "0.85rem",
            color: colors.title,
            mt: 0.25,
          }}
        >
          {card.template || card.title}
        </Typography>
      </Box>

      {pages.map((page, index) => {
        const isLastPage = index === pages.length - 1;
        const renderAddSectionAfter =
          showAddSection && index === addSectionAfterIdx;
        const removable = isRemovablePage(page, index, pageLockConfig);

        return (
          <React.Fragment key={`${page.order}-${page.type}-${index}`}>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  mb: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    color: colors.title,
                    minWidth: 0,
                  }}
                  noWrap
                >
                  Page {page.order}: {formatCreateTaasCardPageType(page.type)}
                </Typography>
                {removable && (
                  <Tooltip title={t("remove", { defaultValue: "Remove page" })}>
                    <IconButton
                      size="small"
                      onClick={() => onRemovePage(index)}
                      aria-label={t("remove", { defaultValue: "Remove page" })}
                      sx={{
                        flexShrink: 0,
                        color: darkMode
                          ? "var(--neutral-400)"
                          : "var(--neutral-600)",
                        border: `1px solid ${
                          darkMode ? "var(--neutral-700)" : "var(--neutral-300)"
                        }`,
                        borderRadius: "50%",
                        p: 0.5,
                        transition:
                          "color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease",
                        "&:hover": {
                          color: darkMode ? "#fca5a5" : "#dc2626",
                          borderColor: darkMode ? "#fca5a5" : "#dc2626",
                          bgcolor: darkMode
                            ? "rgba(248,113,113,0.10)"
                            : "rgba(220,38,38,0.06)",
                        },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: "0.95rem" }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <UseTemplatePageRenderer
                page={page}
                pageIndex={index}
                darkMode={darkMode}
                card={card}
                usesEndingImage={usesEndingImage}
                isUpdatingRef={isUpdatingRef}
                updatePageData={updatePageData}
                t={t}
              />
              <TemplateValidationMessages
                messages={pageValidationMessages[index] || []}
                darkMode={darkMode}
              />
              {!isLastPage && <Divider sx={{ mt: 2 }} />}
            </Box>

            {renderAddSectionAfter && (
              <AddTaasCardPageSection
                types={addablePageTypes}
                onAdd={onAddPage}
                darkMode={darkMode}
                colors={colors}
              />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
}
